import {
  FormContainer,
  FormTabs,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import {
  listImportAutomationSchema,
  TListImportAutomation,
} from "./ListImportAutomationSchema";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  createListImportAutomation,
  getListImportAutomation,
  updateListImportAutomation,
} from "../../../../../services/listImportAutomation.service";
import { Grid } from "@mui/material";
import { format } from "date-fns";
import { BasicData } from "./BasicData/BasicData";
import { useNavigate, useParams } from "react-router-dom";
import { ClassificationPage } from "./ClassificationPage/ClassificationPage";
import StarIcon from "@mui/icons-material/Star";
import { dateOnlyStringToNewDate } from "../../../../../utils/conversions";

const translationPath = "page.4uDlist.listImportAutomation.";

const ListImportAutomation = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { setMessage } = useNotificationContext();

  const { id, eI } = useParams();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createListImportAutomation,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateListImportAutomation,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getListImportAutomation,
  );

  const saveHandler = useCallback(
    (v: TListImportAutomation) => {
      return save({
        ...v,
        initialDate: format(v.initialDate, "yyyy-MM-dd"),
        endDate: format(v.endDate, "yyyy-MM-dd"),
      });
    },
    [save],
  );

  const updateHandler = useCallback(
    (v: TListImportAutomation, id: string) => {
      return update({
        item: {
          ...v,
          id,
          initialDate: format(v.initialDate, "yyyy-MM-dd"),
          endDate: format(v.endDate, "yyyy-MM-dd"),
        },
        id: id,
      });
    },
    [update],
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TListImportAutomation>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TListImportAutomation = {
          ...data,
          initialDate: dateOnlyStringToNewDate(data.initialDate),
          endDate: dateOnlyStringToNewDate(data.endDate),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  let tabs = useMemo(
    () => [
      {
        label: t(translationPath + "classifications"),
        icon: <StarIcon fontSize="small" />,
        component: <ClassificationPage />,
      },
    ],
    [t],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (data) {
        setMessage({
          message: t(translationPath + "created_successfully"),
          type: "success",
        });
        navigate(
          `/e/${eI}/4uDlist/imports/listImportAutomation/${data.id}/edit`,
          {
            replace: true,
          },
        );
      } else {
        navigate(`/e/${eI}/4uDlist/imports/listImportAutomation/`, {
          replace: true,
        });
      }
    },
    [t, id],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <>
      <FormContainer<TListImportAutomation>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "list_import_automation")}
        schema={listImportAutomationSchema}
        subtitle={t(translationPath + "new")}
        subtitleWatchField="name"
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight="fit-content"
        fields={() => (
          <Grid container spacing={2}>
            <BasicData />
          </Grid>
        )}
      />
      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ListImportAutomation;
